// frontend/src/components/QuantixGo/DeviceManagementPage.jsx

import React, { useState, useEffect } from 'react';
import { quantixgoAPI } from '../../api/client';
import { useToast } from '../../hooks/useToast';
import { useTranslation } from '../../context/TranslationContext';
import DataTable from '../Common/DataTable';
import './DeviceRequestsPage.css';

const DeviceManagementPage = () => {
  const { t } = useTranslation();
  const { toast_success, toast_error } = useToast();

  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [statusFilter, setStatusFilter] = useState('active');
  const [searchTerm, setSearchTerm] = useState('');

  const [confirmUnassign, setConfirmUnassign] = useState(null); // device object, or null

  const loadDevices = async () => {
    try {
      setLoading(true);
      const res = await quantixgoAPI.listActiveDevices();
      setDevices(res.data?.data || []);
      setError(null);
    } catch (err) {
      console.error('Error loading devices:', err);
      setError(t('quantixgo.deviceManagement.loadError', 'Falha ao carregar dispositivos'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDevices(); }, []);

  const filteredDevices = devices.filter((d) => {
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && d.is_active) ||
      (statusFilter === 'inactive' && !d.is_active);

    if (!matchesStatus) return false;

    if (!searchTerm.trim()) return true;
    const term = searchTerm.trim().toLowerCase();
    return (
      d.device_code?.toLowerCase().includes(term) ||
      d.description?.toLowerCase().includes(term) ||
      d.salesperson_name?.toLowerCase().includes(term) ||
      d.salesperson_code?.toLowerCase().includes(term)
    );
  });

  const handleUnassign = async () => {
    if (!confirmUnassign) return;
    try {
      await quantixgoAPI.unassignDevice(confirmUnassign.device_id);
      toast_success(t('quantixgo.deviceManagement.unassigned', 'Dispositivo desativado e licença libertada'));
      setConfirmUnassign(null);
      loadDevices();
    } catch (err) {
      console.error('Unassign failed:', err);
      toast_error(err.response?.data?.error || t('quantixgo.deviceManagement.unassignError', 'Falha ao desativar'));
    }
  };

  const getStatusBadge = (device) => {
    if (!device.is_active) {
      return <span className="status-badge status-cancelled">✗ {t('quantixgo.deviceManagement.inactive', 'Inativo')}</span>;
    }
    if (!device.activated_at) {
      return <span className="status-badge status-partial">⏳ {t('quantixgo.deviceManagement.notActivated', 'Não ativado')}</span>;
    }
    return <span className="status-badge status-completed">✓ {t('quantixgo.deviceManagement.active', 'Ativo')}</span>;
  };

  const getLicenseBadge = (device) => {
    if (!device.license_id) {
      return <span className="status-badge status-cancelled">{t('quantixgo.deviceManagement.noLicense', 'Sem licença')}</span>;
    }
    const expired = device.license_expires_at && new Date(device.license_expires_at) < new Date();
    if (expired) {
      return <span className="status-badge status-partial">{t('quantixgo.deviceManagement.licenseExpired', 'Expirada')}</span>;
    }
    return <span className="status-badge status-completed">#{device.license_id}</span>;
  };

  const columns = [
    { header: t('quantixgo.deviceManagement.deviceCode', 'Dispositivo'), field: 'device_code', className: 'code-column' },
    {
      header: t('quantixgo.deviceManagement.description', 'Descrição'),
      field: 'description',
      render: (row) => row.description || <span className="text-muted">—</span>,
    },
    { header: t('quantixgo.deviceManagement.subsidiary', 'Subsidiária'), field: 'subsidiary_code' },
    {
      header: t('quantixgo.deviceManagement.salesperson', 'Vendedor'),
      field: 'salesperson_name',
      render: (row) => `${row.salesperson_code} - ${row.salesperson_name || '—'}`,
    },
    { header: t('common.status', 'Estado'), field: 'status', render: (row) => getStatusBadge(row) },
    { header: t('quantixgo.deviceManagement.license', 'Licença'), field: 'license', render: (row) => getLicenseBadge(row) },
    {
      header: t('quantixgo.deviceManagement.lastSync', 'Última Sincronização'),
      field: 'last_sync_at',
      render: (row) => (row.last_sync_at ? new Date(row.last_sync_at).toLocaleString() : <span className="text-muted">—</span>),
    },
    {
      header: '',
      field: 'actions',
      className: 'text-right',
      render: (row) =>
        row.is_active && (
          <button className="btn-danger" onClick={() => setConfirmUnassign(row)}>
            {t('quantixgo.deviceManagement.unassign', 'Desativar')}
          </button>
        ),
    },
  ];

  return (
    <div className="order-list device-requests-list">
      <div className="order-filters">
        <div className="filters-row">
          <div className="filter-group">
            <label>{t('common.search', 'Pesquisa')}</label>
            <input
              type="text"
              placeholder={t('quantixgo.deviceManagement.searchPlaceholder', 'Dispositivo, descrição, vendedor...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>{t('common.status', 'Estado')}</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="active">{t('quantixgo.deviceManagement.active', 'Ativo')}</option>
              <option value="inactive">{t('quantixgo.deviceManagement.inactive', 'Inativo')}</option>
              <option value="all">{t('common.all', 'Todos')}</option>
            </select>
          </div>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filteredDevices}
        loading={loading}
        error={error}
        onRetry={loadDevices}
        emptyMessage={t('quantixgo.deviceManagement.none', 'Nenhum dispositivo encontrado')}
      />

      {confirmUnassign && (
        <div className="modal-overlay" onClick={() => setConfirmUnassign(null)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <h3>{t('quantixgo.deviceManagement.confirmTitle', 'Desativar Dispositivo')}</h3>
            <p className="modal-subtitle">
              {confirmUnassign.device_code} — {confirmUnassign.salesperson_name || confirmUnassign.salesperson_code}
            </p>
            <p style={{ fontSize: 13, color: 'var(--gray-600)', marginBottom: 16 }}>
              {t(
                'quantixgo.deviceManagement.confirmMessage',
                'O dispositivo deixa de conseguir sincronizar. A licença associada fica livre para ser atribuída a outro dispositivo. O histórico local do tablet não é afetado.'
              )}
            </p>
            <div className="modal-actions">
              <button className="btn-danger" onClick={handleUnassign}>
                {t('quantixgo.deviceManagement.confirmUnassign', 'Confirmar Desativação')}
              </button>
              <button className="btn-secondary" onClick={() => setConfirmUnassign(null)}>
                {t('common.cancel', 'Cancelar')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceManagementPage;